/**
 * apiCache.js
 * ===========
 * İki katmanlı cache:
 *  1. Memory (session): Aynı sekme içinde anında
 *  2. localStorage (persist): Sayfa yenilemelerinde bile hızlı ilk render
 *
 * Lookup verileri (piyasalar, para_birimleri, periyotlar) için TTL: 10 dakika
 * Supabase JWT token için TTL: 50 dakika
 */

import { supabase } from "../supabaseClient";
import axios from "axios";
import { API_URL } from "../config";

// ─── Memory cache ─────────────────────────────────────────────────────────
const memCache = new Map();

// ─── localStorage helper'ları ──────────────────────────────────────────────
const LS_PREFIX = "borsa_cache:";

function lsGet(key) {
    try {
        const raw = localStorage.getItem(LS_PREFIX + key);
        if (!raw) return null;
        const { value, expires } = JSON.parse(raw);
        if (Date.now() > expires) {
            localStorage.removeItem(LS_PREFIX + key);
            return null;
        }
        return value;
    } catch {
        return null;
    }
}

function lsSet(key, value, ttlMs) {
    try {
        localStorage.setItem(LS_PREFIX + key, JSON.stringify({
            value,
            expires: Date.now() + ttlMs,
        }));
    } catch {
        // localStorage dolu olabilir, sessizce geç
    }
}

// ─── JWT Token ────────────────────────────────────────────────────────────
const TOKEN_TTL_MS = 50 * 60 * 1000; // 50 dakika

export async function getToken() {
    const cached = memCache.get("__token__");
    if (cached && Date.now() < cached.expires) {
        return cached.value;
    }
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token || null;
    if (token) {
        memCache.set("__token__", { value: token, expires: Date.now() + TOKEN_TTL_MS });
    }
    return token;
}

// ─── Genel API cache ──────────────────────────────────────────────────────
const LOOKUP_TTL_MS = 10 * 60 * 1000; // 10 dakika

/**
 * Önce memory, sonra localStorage, yoksa fetchFn çağır.
 * fetchFn: () => Promise<data>
 */
export async function cachedFetch(key, fetchFn, ttlMs = LOOKUP_TTL_MS) {
    // 1. Memory hit → anında dön
    const mem = memCache.get(key);
    if (mem && Date.now() < mem.expires) {
        return mem.value;
    }

    // 2. localStorage hit → hemen dön, arka planda refresh başlat
    const ls = lsGet(key);
    if (ls !== null) {
        // Memory'e de yükle
        memCache.set(key, { value: ls, expires: Date.now() + ttlMs });
        // Arka planda sessizce yenile (stale-while-revalidate)
        fetchFn().then(fresh => {
            memCache.set(key, { value: fresh, expires: Date.now() + ttlMs });
            lsSet(key, fresh, ttlMs);
        }).catch(() => {});
        return ls;
    }

    // 3. Cache miss → fetch et, her iki cache'e yaz
    const data = await fetchFn();
    memCache.set(key, { value: data, expires: Date.now() + ttlMs });
    lsSet(key, data, ttlMs);
    return data;
}

/**
 * Belirli bir key'i her iki cache'den de sil.
 */
export function cacheInvalidate(key) {
    memCache.delete(key);
    try { localStorage.removeItem(LS_PREFIX + key); } catch {}
}

/**
 * Tüm borsa cache'ini temizle.
 */
export function cacheClearAll() {
    memCache.clear();
    try {
        Object.keys(localStorage)
            .filter(k => k.startsWith(LS_PREFIX))
            .forEach(k => localStorage.removeItem(k));
    } catch {}
}

// ─── Lookup yardımcıları ─────────────────────────────────────────────────

// Varsayılan değerler — endpoint erişilemezse bunlar kullanılır
const FALLBACK = {
    piyasalar:            ["BIST", "Kripto"],
    para_birimleri_BIST:  ["USD", "TL", "BIST100 (TL)", "BIST100 (USD)"],
    para_birimleri_Kripto:["USD", "USD (Futures)"],
    para_birimleri_tum:   ["TL", "USD", "Euro", "BIST100 (TL)", "BIST100 (USD)", "USD (Futures)"],
    periyotlar:           ["1 Yıl", "6 Ay", "3 Ay", "1 Ay"],
};

export async function getLookupPiyasalar() {
    try {
        const token = await getToken();
        return await cachedFetch("lookup:piyasalar", () =>
            axios.get(`${API_URL}/bakim/lookup/piyasalar`, {
                headers: { Authorization: `Bearer ${token}` }
            }).then(r => r.data.map(p => p.piyasa))
        );
    } catch {
        return FALLBACK.piyasalar;
    }
}

export async function getLookupParaBirimleri(piyasa, modul) {
    try {
        const token = await getToken();
        return await cachedFetch(`lookup:para_birimleri:${piyasa || "tum"}:${modul || "tum"}`, () =>
            axios.get(`${API_URL}/bakim/lookup/para-birimleri`, {
                params: { piyasa, modul },
                headers: { Authorization: `Bearer ${token}` }
            }).then(r => r.data.map(p => p.para_birimi))
        );
    } catch {
        const key = piyasa === "BIST" ? "para_birimleri_BIST"
                  : piyasa === "Kripto" ? "para_birimleri_Kripto"
                  : "para_birimleri_tum";
        return FALLBACK[key];
    }
}

export async function getLookupPeriyotlar(modul) {
    try {
        const token = await getToken();
        return await cachedFetch(`lookup:periyotlar:${modul || "tum"}`, () =>
            axios.get(`${API_URL}/bakim/lookup/periyotlar`, {
                params: { modul },
                headers: { Authorization: `Bearer ${token}` }
            }).then(r => r.data.map(p => p.periyot))
        );
    } catch {
        return FALLBACK.periyotlar;
    }
}
